/**
 * Dashboard Page (Intelligence)
 * Executive overview dashboard
 */
import React from 'react';
import { 
  LayoutDashboard, 
  FileText, 
  CheckCircle2,
  AlertTriangle,
  Clock,
  TrendingUp,
  BarChart3,
  Calendar,
  Zap
} from 'lucide-react';

const CYCLE_SUMMARY = [
  { name: 'FY2024 CSRD/ESRS', status: 'evidence', progress: 65, daysLeft: 45 },
  { name: '2025 CDP Climate', status: 'mapping', progress: 35, daysLeft: 76 },
  { name: 'FY2024 TCFD', status: 'audit', progress: 85, daysLeft: 12 },
];

const QUICK_STATS = {
  activeCycles: 4,
  openTasks: 23,
  urgentItems: 3,
  complianceScore: 92,
};

export default function IntelligenceDashboardPage() {
  return (
    <div className="space-y-6" data-testid="intelligence-dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Executive Dashboard</h1>
        <p className="text-sm text-[#6B6B6B] mt-1">
          High-level overview of your compliance and disclosure status
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-emerald-500/10 to-transparent border border-emerald-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <FileText size={20} className="text-emerald-400" />
            <span className="text-sm font-medium text-[#A0A0A0]">Active Cycles</span>
          </div>
          <div className="text-3xl font-bold text-white">{QUICK_STATS.activeCycles}</div>
        </div>
        <div className="bg-gradient-to-br from-purple-500/10 to-transparent border border-purple-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <Zap size={20} className="text-purple-400" />
            <span className="text-sm font-medium text-[#A0A0A0]">Open Tasks</span>
          </div>
          <div className="text-3xl font-bold text-white">{QUICK_STATS.openTasks}</div>
        </div>
        <div className="bg-gradient-to-br from-red-500/10 to-transparent border border-red-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <AlertTriangle size={20} className="text-red-400" />
            <span className="text-sm font-medium text-[#A0A0A0]">Urgent Items</span>
          </div>
          <div className="text-3xl font-bold text-red-400">{QUICK_STATS.urgentItems}</div>
        </div>
        <div className="bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-xl p-5">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 size={20} className="text-blue-400" />
            <span className="text-sm font-medium text-[#A0A0A0]">Compliance Score</span>
          </div>
          <div className="text-3xl font-bold text-emerald-400">{QUICK_STATS.complianceScore}%</div>
        </div>
      </div>

      {/* Active Cycles Summary */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-white">Active Disclosure Cycles</h3>
          <button className="text-xs text-emerald-400 hover:underline">View All</button>
        </div>
        <div className="space-y-4">
          {CYCLE_SUMMARY.map((cycle, idx) => (
            <div key={idx} className="flex items-center gap-4">
              <div className="flex-1">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-sm font-medium text-white">{cycle.name}</span>
                  <span className={`text-xs ${cycle.daysLeft < 30 ? 'text-red-400' : 'text-[#6B6B6B]'}`}>
                    {cycle.daysLeft} days left
                  </span>
                </div>
                <div className="h-2 bg-[#1F1F1F] rounded-full overflow-hidden">
                  <div 
                    className={`h-full rounded-full ${cycle.progress >= 80 ? 'bg-emerald-500' : cycle.progress >= 50 ? 'bg-amber-500' : 'bg-blue-500'}`}
                    style={{ width: `${cycle.progress}%` }}
                  />
                </div>
              </div>
              <span className="text-sm font-medium text-white w-12 text-right">{cycle.progress}%</span>
            </div>
          ))}
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-2 gap-4">
        {/* Recent Activity */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white">Recent Activity</h3>
            <Clock size={14} className="text-[#6B6B6B]" />
          </div>
          <div className="space-y-3">
            {[
              { text: 'Evidence uploaded: Q4 Emissions Report', time: '2h ago' },
              { text: 'Mapping completed for ESRS E1-6', time: '4h ago' },
              { text: 'Supplier survey sent to 12 vendors', time: '1d ago' },
              { text: 'Audit pack generated for TCFD', time: '2d ago' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-[#1F1F1F] last:border-0">
                <span className="text-sm text-[#A0A0A0]">{item.text}</span>
                <span className="text-xs text-[#6B6B6B]">{item.time}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Upcoming Deadlines */}
        <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-white">Upcoming Deadlines</h3>
            <Calendar size={14} className="text-[#6B6B6B]" />
          </div>
          <div className="space-y-3">
            {[
              { text: 'TCFD Report Submission', date: 'Apr 15', status: 'urgent' },
              { text: 'CSRD Evidence Complete', date: 'May 1', status: 'upcoming' },
              { text: 'CDP Response Due', date: 'Jul 31', status: 'planned' },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-[#1F1F1F] last:border-0">
                <span className="text-sm text-[#A0A0A0]">{item.text}</span>
                <div className="flex items-center gap-2">
                  <span className={`px-2 py-0.5 text-[10px] font-medium rounded ${
                    item.status === 'urgent' ? 'bg-red-500/20 text-red-400' :
                    item.status === 'upcoming' ? 'bg-amber-500/20 text-amber-400' :
                    'bg-blue-500/20 text-blue-400'
                  }`}>
                    {item.date}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Chart Placeholder */}
      <div className="bg-[#111111] border border-[#1F1F1F] rounded-xl p-5">
        <h3 className="text-base font-semibold text-white mb-4">Compliance Trend</h3>
        <div className="h-48 flex items-center justify-center bg-[#0A0A0A] rounded-lg">
          <div className="text-center">
            <BarChart3 size={48} className="mx-auto text-[#2F2F2F] mb-2" />
            <p className="text-sm text-[#6B6B6B]">Chart visualization coming soon</p>
          </div>
        </div>
      </div>
    </div>
  );
}
